import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Video, Loader2, RefreshCw, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  created: { label: 'En attente', color: 'bg-slate-500/15 text-slate-600' },
  joining: { label: 'Rejoint la réunion', color: 'bg-blue-500/15 text-blue-600' },
  waiting_room: { label: "Salle d'attente", color: 'bg-amber-500/15 text-amber-600' },
  in_call: { label: 'En appel (sans enregistrement)', color: 'bg-amber-500/15 text-amber-600' },
  recording: { label: 'Enregistrement en cours', color: 'bg-emerald-500/15 text-emerald-600' },
  call_ended: { label: 'Réunion terminée', color: 'bg-purple-500/15 text-purple-600' },
  recording_ready: { label: 'Enregistrement prêt', color: 'bg-indigo-500/15 text-indigo-600' },
  done: { label: 'Mémoire créée', color: 'bg-emerald-500/15 text-emerald-700' },
  fatal: { label: 'Erreur', color: 'bg-red-500/15 text-red-600' },
  permission_denied: { label: 'Refusé par hôte', color: 'bg-red-500/15 text-red-600' },
};

const ACTIVE_STATES = new Set(['created', 'joining', 'waiting_room', 'in_call', 'recording', 'call_ended', 'recording_ready']);

function StatusPill({ status, transcriptStatus }) {
  const meta = STATUS_LABELS[status] || { label: status || 'Inconnu', color: 'bg-slate-500/15 text-slate-600' };
  return (
    <div className="flex items-center gap-2">
      <Badge className={`${meta.color} border-0 font-medium`} data-testid={`recall-status-${status}`}>
        {meta.label}
      </Badge>
      {transcriptStatus === 'imported' && (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
          <CheckCircle2 className="w-3 h-3" /> transcript
        </span>
      )}
      {transcriptStatus === 'failed' && (
        <span className="inline-flex items-center gap-1 text-xs text-red-600">
          <AlertCircle className="w-3 h-3" /> erreur transcript
        </span>
      )}
    </div>
  );
}

function isValidMeetingUrl(url) {
  if (!url) return false;
  const patterns = [
    /^https:\/\/teams\.microsoft\.com\//i,
    /^https:\/\/teams\.live\.com\//i,
    /^https:\/\/.*\.zoom\.us\/j\//i,
    /^https:\/\/zoom\.us\/j\//i,
    /^https:\/\/meet\.google\.com\//i,
    /^https:\/\/.+\.webex\.com\//i,
  ];
  return patterns.some((p) => p.test(url.trim()));
}

export default function RecallMeetingsSection({ personId, token, canManage = true }) {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBots = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recall/bots`, { headers, params: { person_id: personId, limit: 10 } });
      setBots(res.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [personId, token]);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  // Auto-refresh while at least one bot is in an active (non-terminal) state
  useEffect(() => {
    const hasActive = bots.some((b) => ACTIVE_STATES.has(b.status));
    if (!hasActive) return;
    const id = setInterval(fetchBots, 8000);
    return () => clearInterval(id);
  }, [bots, fetchBots]);

  const launchBot = async () => {
    if (!isValidMeetingUrl(meetingUrl)) {
      toast.error('URL invalide. Utilise un lien Teams, Zoom, Google Meet ou Webex.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/recall/bot/create`,
        { meeting_url: meetingUrl.trim(), person_id: personId },
        { headers }
      );
      toast.success('Bot lancé. Il rejoint la réunion dans ~30 secondes.');
      setMeetingUrl('');
      await fetchBots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du lancement du bot');
    } finally {
      setSubmitting(false);
    }
  };

  const refreshBot = async (bot) => {
    setRefreshing((r) => ({ ...r, [bot.recall_bot_id]: true }));
    try {
      await axios.post(`${API}/recall/bot/${bot.recall_bot_id}/refresh`, {}, { headers });
      await fetchBots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh impossible');
    } finally {
      setRefreshing((r) => ({ ...r, [bot.recall_bot_id]: false }));
    }
  };

  return (
    <Card data-testid="recall-meetings-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-500/10">
              <Video className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <CardTitle className="text-base font-heading">Réunions (Recall.ai)</CardTitle>
              <CardDescription className="text-xs">
                Bot universel Teams / Zoom / Meet — transcription auto vers la mémoire RAG
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {canManage && (
          <div className="flex gap-2">
            <Input
              placeholder="Coller un lien Teams / Zoom / Meet..."
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !submitting && launchBot()}
              disabled={submitting}
              className="font-mono text-xs"
              data-testid="recall-meeting-url-input"
            />
            <Button
              onClick={launchBot}
              disabled={submitting || !meetingUrl.trim()}
              data-testid="recall-launch-bot-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lancer'}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
          </div>
        ) : bots.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            Aucune réunion enregistrée pour ce clone. Lance ton premier bot ci-dessus.
          </p>
        ) : (
          <div className="space-y-2" data-testid="recall-bots-list">
            {bots.map((bot) => {
              const isActive = ACTIVE_STATES.has(bot.status);
              return (
                <div
                  key={bot.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card/30"
                  data-testid={`recall-bot-${bot.recall_bot_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusPill status={bot.status} transcriptStatus={bot.transcript_status} />
                      {isActive && (
                        <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate font-mono">
                      {bot.meeting_url}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Créé le {new Date(bot.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => refreshBot(bot)}
                      disabled={refreshing[bot.recall_bot_id]}
                      title="Rafraîchir le statut depuis Recall"
                      data-testid={`recall-refresh-${bot.recall_bot_id}`}
                    >
                      {refreshing[bot.recall_bot_id] ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    {bot.meeting_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(bot.meeting_url, '_blank')}
                        title="Ouvrir le lien"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
